import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Award, Lock, Phone, MessageCircle, Mail, Music, Video, FileText, Languages } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DiyaLoader, Toranam } from "@/components/Ornaments";
import { toast } from "sonner";

export default function ArchakaDetail() {
  const { id } = useParams();
  const { t } = useApp();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [a, setA] = useState(null);

  const load = async () => { try { const { data } = await api.get(`/archakas/${id}`); setA(data); } catch (e) { toast.error("అర్చకుడు కనబడలేదు"); } };
  useEffect(() => { load(); }, [id]);


  if (!a) return <div className="parchment-texture min-h-screen"><DiyaLoader /></div>;
  const livingMap = { family: `👨‍👩‍👦 ${t("with_family")}`, bachelor: `🧘 ${t("bachelor")}`, both: `🕉️ ${t("open_both")}` };
  const ytEmbed = (u) => { const m = (u || "").match(/(?:youtu\.be\/|v=)([\w-]{11})/); return m ? `https://www.youtube.com/embed/${m[1]}` : null; };

  return (
    <div className="parchment-texture min-h-screen pb-10">
      <div className="bg-gradient-to-r from-maroon to-saffron py-8">
        <div className="max-w-5xl mx-auto px-6 flex items-center gap-4">
          <Avatar className="w-24 h-24 border-4 border-marigold"><AvatarImage src={a.photo} /><AvatarFallback className="bg-maroon text-marigold text-2xl">{(a.name || "అ").slice(0, 2)}</AvatarFallback></Avatar>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white">{a.name}</h1>
            <p className="text-marigold font-telugu flex items-center gap-1"><MapPin className="w-4 h-4" />{a.district}, {a.state}</p>
            <Badge className="mt-2 lineage-badge font-telugu">{livingMap[a.living_preference]}</Badge>
          </div>
        </div>
      </div>
      <Toranam />

      <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="p-5 rounded-2xl lineage-badge font-telugu text-base">
            🪷 <b>గోత్రం:</b> {a.gotram}{a.gotram_custom ? ` (${a.gotram_custom})` : ""}
            {a.shakha && <> &nbsp;|&nbsp; <b>శాఖ:</b> {a.shakha}</>}
            {a.sutram && <> &nbsp;|&nbsp; <b>సూత్రం:</b> {a.sutram}</>}
          </div>

          <div className="bg-surface rounded-2xl gold-border temple-shadow p-6 space-y-3">
            <h3 className="font-serifte font-bold text-maroon text-lg">వివరాలు</h3>
            <div className="flex flex-wrap gap-2">
              {a.agama && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu">{a.agama}</Badge>}
              {a.experience_years ? <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Award className="w-3 h-3 mr-1" />{a.experience_years} {t("years")}</Badge> : null}
              {a.languages && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Languages className="w-3 h-3 mr-1" />{a.languages}</Badge>}
            </div>
            {a.specializations && <p className="text-sm font-telugu text-muted-foreground">✨ నైపుణ్యాలు: {a.specializations}</p>}
            {a.bio && <p className="text-sm font-telugu text-maroon leading-relaxed">{a.bio}</p>}
          </div>

          {a.mantra_audio && (
            <div className="bg-surface rounded-2xl gold-border temple-shadow p-6">
              <h3 className="font-serifte font-bold text-maroon text-lg mb-2"><Music className="w-5 h-5 inline mr-1 text-saffron" />మంత్ర పఠనం</h3>
              <audio data-testid="archaka-audio" controls src={a.mantra_audio} className="w-full" />
            </div>
          )}

          {a.pooja_videos?.length > 0 && (
            <div className="bg-surface rounded-2xl gold-border temple-shadow p-6">
              <h3 className="font-serifte font-bold text-maroon text-lg mb-3"><Video className="w-5 h-5 inline mr-1 text-saffron" />పూజా వీడియోలు</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {a.pooja_videos.map((v, i) => ytEmbed(v) ? <iframe key={i} src={ytEmbed(v)} className="w-full aspect-video rounded-lg gold-border" allowFullScreen title={`video-${i}`} /> : <video key={i} src={v} controls className="w-full rounded-lg gold-border" />)}
              </div>
            </div>
          )}

          {a.certifications?.length > 0 && (
            <div className="bg-surface rounded-2xl gold-border temple-shadow p-6">
              <h3 className="font-serifte font-bold text-maroon text-lg mb-3"><FileText className="w-5 h-5 inline mr-1 text-saffron" />ధృవీకరణ పత్రాలు</h3>
              <div className="flex flex-wrap gap-2">{a.certifications.map((c, i) => <a key={i} href={c} target="_blank" rel="noreferrer"><img src={c} alt="cert" className="w-24 h-24 object-cover rounded-lg gold-border" /></a>)}</div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-surface rounded-2xl gold-border temple-shadow p-6">
            <h3 className="font-serifte font-bold text-maroon text-lg mb-3">📞 సంప్రదింపు</h3>
            {a.unlocked ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-saffron" /><span className="text-maroon font-medium">{a.mobile}</span></div>
                {a.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-saffron" /><span className="text-maroon break-all">{a.email}</span></div>}
                <a data-testid="archaka-call-btn" href={`tel:${a.mobile}`}><Button className="w-full btn-sacred border-0 font-telugu mb-2"><Phone className="w-4 h-4 mr-2" />{t("call_now")}</Button></a>
                <a data-testid="archaka-wa-btn" href={`https://wa.me/91${a.mobile}`} target="_blank" rel="noreferrer"><Button className="w-full bg-[#25D366] hover:bg-[#1da851] text-white border-0 font-telugu"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</Button></a>
              </div>
            ) : (
              <div className="text-center">
                <div className="p-4 rounded-xl bg-marigold/10 mb-3">
                  <Lock className="w-8 h-8 text-saffron mx-auto mb-2" />
                  <p className="lock-blur text-maroon text-lg">98480 12345</p>
                  <p className="text-xs text-muted-foreground font-telugu mt-2" data-testid="archaka-contact-private">అర్చకుల కాంటాక్ట్ వివరాలు గోప్యంగా ఉంటాయి. మీ ప్రకటన చూసి అర్చకులే మిమ్మల్ని సంప్రదిస్తారు.</p>
                </div>
                {user?.role === "temple" && <Button data-testid="archaka-post-vacancy" onClick={() => nav("/post-vacancy")} className="w-full btn-sacred border-0 font-telugu">ఉచితంగా ఖాళీ ప్రకటన ఇవ్వండి</Button>}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
